import type { EliteOnboardingStatusResponse } from '../../../../types/misc.types'

const FAMILY_ORANGE_STRIP_DEFAULT_COPY = {
  headline: 'EXCLUSIVE FOR FAMILY',
  subline: 'A special price, for your loved ones'
} as const

/** Use with `useEliteOnboardingAdvisoryStatus` on the family landing route (not part of the view props). */
export const FAMILY_LANDING_ONBOARDING_QUERY_KEY = 'NEW_ELITE_FAMILY_ONBOARDING_ADVISORY_STATUS'

/** Use with `useEliteOnboardingAdvisoryStatus` on the family dashboard route (not part of the view props). */
export const FAMILY_DASHBOARD_ONBOARDING_QUERY_KEY = 'NEW_ELITE_FAMILY_DASHBOARD_ONBOARDING_ADVISORY_STATUS'

/**
 * View props derived from elite onboarding for family landing and dashboard (same shape for both).
 *
 * 1. `isFamilyMode` — `throughFamilyAccess` from onboarding.
 * 2. `secondaryUserName` — raw `secondaryUserName` from onboarding (trimmed); use for copy / offer card.
 * 3. `familyOrangeStripText` — two lines for the orange strip, or `null` when that strip should not render.
 * 4. `isEliteBlockedBecausePrimaryNotElite` — true when family access and primary is definitely not Elite
 *    (secondary cannot proceed until primary has Elite); false otherwise.
 */
export type FamilyOnboardingViewProps = {
  isFamilyMode: boolean
  secondaryUserName: string
  familyOrangeStripText: null | { headline: string; subline: string }
  isEliteBlockedBecausePrimaryNotElite: boolean
}

export type FamilyLandingProps = FamilyOnboardingViewProps
export type FamilyDashboardProps = FamilyOnboardingViewProps

export type FamilyOrangeStripTwoLineText = NonNullable<FamilyOnboardingViewProps['familyOrangeStripText']>

export function buildFamilyOnboardingViewProps(
  data: EliteOnboardingStatusResponse | null | undefined
): FamilyOnboardingViewProps {
  if (data == null) {
    return {
      isFamilyMode: false,
      secondaryUserName: '',
      familyOrangeStripText: null,
      isEliteBlockedBecausePrimaryNotElite: false
    }
  }

  const isFamilyMode = Boolean(data?.throughFamilyAccess)
  const secondaryUserName = data?.secondaryUserName?.trim()?.split(' ')?.[0] ?? ''
  const primaryIsEliteOrUnknown = data?.isPrimaryUserElite !== false
  const primaryIsDefinitelyNotElite = data?.isPrimaryUserElite === false

  let familyOrangeStripText: null | { headline: string; subline: string }
  if (isFamilyMode && primaryIsEliteOrUnknown) {
    familyOrangeStripText = { ...FAMILY_ORANGE_STRIP_DEFAULT_COPY }
  } else {
    familyOrangeStripText = null
  }

  const isEliteBlockedBecausePrimaryNotElite = data?.eliteLandingPageState === 'FAMILY_ONBOARDING_NOT_ALLOWED'

  return {
    isFamilyMode,
    secondaryUserName,
    familyOrangeStripText,
    isEliteBlockedBecausePrimaryNotElite
  }
}

/** Non-family default for plan selector embeds (renewal, etc.). */
export const defaultNonFamilyOnboardingViewProps: FamilyOnboardingViewProps = buildFamilyOnboardingViewProps(undefined)
